/**
 * suggestCategory
 * Client-side fuzzy matching to suggest a task category based on task title.
 * Uses tokenized word overlap scoring — no external dependencies.
 * 
 * Pure utility — no React, no platform code. Works in browser and React Native.
 */

// ============================================================================
// Types
// ============================================================================

export interface CategoryInput {
  id: string;
  name: string;
}

export interface CategorySuggestion {
  id: string;
  name: string;
  /** Confidence score between 0 and 1 */
  confidence: number;
}

// ============================================================================
// Constants
// ============================================================================

/** Minimum confidence threshold to return a suggestion */
const CONFIDENCE_THRESHOLD = 0.5;

/** Score weights for different match types */
const EXACT_MATCH_SCORE = 1.0;
const PREFIX_MATCH_SCORE = 0.7;
const SUBSTRING_MATCH_SCORE = 0.4;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Tokenize a string into lowercase words, removing common stop words.
 */
function tokenize(text: string): string[] {
  const stopWords = new Set([
    'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'is', 'it', 'this', 'that', 'be', 'as',
  ]);

  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 1 && !stopWords.has(word));
}

/**
 * Score how well a task token matches a category token.
 * Returns the best match score (0 if no match).
 */
function scoreTokenMatch(taskToken: string, categoryToken: string): number {
  // Exact match
  if (taskToken === categoryToken) return EXACT_MATCH_SCORE;

  // Prefix match (e.g., "phone" matches "phonebank", "bank" matches "banking")
  if (categoryToken.startsWith(taskToken) || taskToken.startsWith(categoryToken)) {
    return PREFIX_MATCH_SCORE;
  }

  // Substring match (e.g., "knock" in "knocking")
  if (categoryToken.includes(taskToken) || taskToken.includes(categoryToken)) {
    return SUBSTRING_MATCH_SCORE;
  }

  return 0;
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Suggest the best-matching category for a task title using fuzzy word overlap.
 * 
 * @param taskTitle - The task title to match against categories
 * @param categories - Available categories to match against
 * @returns The best matching category with confidence score, or null if no confident match
 * 
 * @example
 * suggestCategory("Schedule phone bank shift", [
 *   { id: "1", name: "Phone Banking" },
 *   { id: "2", name: "Door Knocking" },
 * ])
 * // { id: "1", name: "Phone Banking", confidence: 0.85 }
 * 
 * @example
 * suggestCategory("Prep voter lists", [
 *   { id: "1", name: "Phone Banking" },
 *   { id: "2", name: "Data Entry" },
 * ])
 * // null — no confident fuzzy match (AI fallback needed)
 */
export function suggestCategory(
  taskTitle: string,
  categories: CategoryInput[]
): CategorySuggestion | null {
  if (!taskTitle.trim() || categories.length === 0) return null;

  const titleTokens = tokenize(taskTitle);
  if (titleTokens.length === 0) return null;

  let bestMatch: CategorySuggestion | null = null;

  for (const category of categories) {
    const categoryTokens = tokenize(category.name);
    if (categoryTokens.length === 0) continue;

    // For each category token, find the best matching task token
    let totalScore = 0;
    for (const catToken of categoryTokens) {
      let bestTokenScore = 0;
      for (const titleToken of titleTokens) {
        const score = scoreTokenMatch(titleToken, catToken);
        if (score > bestTokenScore) {
          bestTokenScore = score;
        }
      }
      totalScore += bestTokenScore;
    }

    // Normalize by category token count
    const confidence = totalScore / categoryTokens.length;

    if (confidence >= CONFIDENCE_THRESHOLD && (!bestMatch || confidence > bestMatch.confidence)) {
      bestMatch = {
        id: category.id,
        name: category.name,
        confidence,
      };
    }
  }

  return bestMatch;
}
